<template>
    <template v-for="cond in filteredConditions" v-bind:key="cond.CCId">
        <img width="16" height="16"
            @mouseover="e => setCondTooltip(e.target! as HTMLElement, cond)"
            @mouseleave="e => setCondTooltip(e.target! as HTMLElement, undefined)"
            @click="e => onClickCond(e as MouseEvent, cond)"
            :src='condIconUrl(cond.CCId)' />
    </template>

    <v-tooltip v-if="condTooltip" v-model="condTooltipValue" :activator="condTooltipParent">
        {{ condTooltipText(condTooltip) }}
    </v-tooltip>
</template>

<script lang="ts">
import { defineComponent, PropType, inject, ref, computed, Ref } from 'vue';
import type { EntityCondition } from '@/eventActor';
import { addHiddenCC } from '@/store';
import { condIconUrl as resolveCondIconUrl } from '@/effectState';

export default defineComponent({
    props: {
        conditions: {
            type: Array as PropType<EntityCondition[]>,
            required: true,
        },
    },
    setup(props) {
        const region = inject('region') as Ref<string> | string;
        const condNameMap = inject('condNameMap') as Ref<Record<number, string>>;
        const itemNameMap = inject('itemNameMap') as Ref<Record<number, string>>;
        const hiddenCCIds = inject('hiddenCCIds') as Ref<Set<number>>;

        // 合成 buff（effect-state）走技能圖；其餘走預設 characterconditionimage
        const condIconUrl = (ccId: number) => {
            const r = typeof region === 'string' ? region : region.value;
            return resolveCondIconUrl(r, ccId);
        };

        // tooltip：一般 condition 顯示名稱；合成 buff 額外附上來源樂譜名（如「樂器演奏（特殊樂譜卷軸）」）
        const condTooltipText = (cond: EntityCondition) => {
            const name = condNameMap.value[cond.CCId] ?? `CC ${cond.CCId}`;
            if (cond.SourceItemId !== undefined) {
                const item = itemNameMap.value[cond.SourceItemId];
                if (item) return `${name}（${item}）`;
            }
            return name;
        };

        const filteredConditions = computed(() =>
            props.conditions.filter(c => !hiddenCCIds.value.has(c.CCId))
        );

        const condTooltipParent = ref<HTMLElement>();
        const condTooltipValue = ref(false);
        const condTooltip = ref<EntityCondition>();

        const setCondTooltip = (el: HTMLElement, cond?: EntityCondition) => {
            condTooltip.value = cond;
            condTooltipParent.value = el;
            condTooltipValue.value = !!cond;
        }

        const onClickCond = (e: MouseEvent, cond: EntityCondition) => {
            if (e.shiftKey) {
                const name = condNameMap.value[cond.CCId] ?? `CC ${cond.CCId}`;
                if (confirm(`${name}을(를) 숨기시겠습니까?`)) {
                    addHiddenCC(cond.CCId);
                }
                return;
            }
            setCondTooltip(e.target! as HTMLElement, cond);
        }

        return {
            region,
            condIconUrl,
            condNameMap,
            condTooltipText,
            filteredConditions,
            condTooltip,
            condTooltipParent,
            condTooltipValue,
            setCondTooltip,
            onClickCond,
        }
    }
});

</script>
